import { Spinner } from '@/components/ui/_refactor/skeleton/spinner';
import { cn } from '@/utils/cn';
import { handleDomBubbleEvent } from '@/utils/dom';
import React from 'react';

type TTicketTabItem<T extends string> = {
  label: string;
  value: T;
  count?: number;
  isLoading?: boolean;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

type TTicketTabsProps<T extends string> = {
  items: TTicketTabItem<T>[];
  activeValue: T;
  containerClassName?: string;
  iconClassName?: string;
  textClassName?: string;
  buttonClassName?: string;
  onChange: (value: T) => void;
};

function TicketTab<T extends string>({
  items,
  activeValue,
  iconClassName,
  containerClassName,
  textClassName,
  buttonClassName,
  onChange,
}: TTicketTabsProps<T>) {
  return (
    <div className={cn('flex flex-row border-b border-[#E2E8F0] ', containerClassName)}>
      {items.map((item, index) => {
        const isActive = item.value === activeValue;
        const Icon = item.icon;

        return (
          <button
            key={index + 1}
            type="button"
            onClick={(e) => {
              handleDomBubbleEvent(e);
              onChange(item.value);
            }}
            className={cn(
              'flex flex-1 cursor-pointer flex-row items-center justify-center gap-[6px] py-[8px]',
              {
                'border-b-[2px] border-[#0C8DBF]': isActive,
                'border-b-[2px] border-transparent': !isActive,
              },
              buttonClassName
            )}
          >
            {Icon ? (
              <Icon
                className={cn(
                  'w-[16px] h-[16px] flex-shrink-0 text-[#9D9D9C]',
                  {
                    'text-[#0C8DBF]': isActive,
                    'text-[#9D9D9C]': !isActive,
                  },
                  iconClassName
                )}
              />
            ) : null}
            <span
              className={cn(
                'text-[12px] font-[700] text-[#9D9D9C]',
                {
                  'text-[#0C8DBF]': isActive,
                  'text-[#9D9D9C]': !isActive,
                },
                textClassName
              )}
            >
              {item.label}
            </span>

            {item.count && item.count > 0 && !item.isLoading ? (
              <div
                className={cn(
                  'rounded-full cursor-pointer text-[11px] w-4 h-4 flex items-center justify-center bg-[#E4F8FF] text-[#0C8DBF]',
                  {
                    'bg-gray-200 text-gray-500': item.isLoading,
                    'bg-[#E4F8FF] text-[#0C8DBF]': !item.isLoading,
                  }
                )}
              >
                {item.count || 0}
              </div>
            ) : null}

            {item.isLoading ? (
              <Spinner
                isLoading={item.isLoading}
                className="w-4 h-4 border-[1.5px]"
                borderColor="border-gray-200"
                borderTopColor="border-t-[#0C8DBF]"
                loadingBorderTopColor="border-t-[#0C8DBF]"
              />
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export default TicketTab;
